import { z } from '@/utils/zod'

export const LocalAuthProviderTypeSchema = z.literal('local')

export type LocalAuthProviderType = z.infer<typeof LocalAuthProviderTypeSchema>

// IMPORTANT: Define password strengths in ascending order of security, from weakest to strongest
export const PasswordStrengthSchema = z.enum(['none', 'low', 'fair', 'good', 'excellent'])

export type PasswordStrength = z.infer<typeof PasswordStrengthSchema>

export const MfaFactorSchema = z.enum(['totp', 'email'])

export type MfaFactor = z.infer<typeof MfaFactorSchema>

export const LocalAuthProviderSchema = z.object({
    type: LocalAuthProviderTypeSchema,

    password: z.object({
        strength: PasswordStrengthSchema,
        minLength: z.number().min(1).max(128),
        requireConfirmation: z.boolean(),
        reset: z.object({
            tokenTtl: z.number().min(1), // seconds
        }),
    }),

    emailVerification: z.toggleable({
        requirement: z.enum(['optional', 'required']),
        tokenTtl: z.number().min(1), // seconds
        resendWait: z.number().min(15), // seconds
    }),

    signup: z.toggleable({
        blockDisposableEmails: z.boolean(),
        captcha: z.toggleable({
            method: z.enum(['recaptchaV3']),
            key: z.string().min(1),
            secret: z.string().min(1),
            minScore: z.number().min(0).max(1),
        }),
    }),

    mfa: z.toggleable({
        requirement: z.enum(['optional', 'required']),
        promptAtSignup: z.boolean(),
        factors: z.array(MfaFactorSchema).min(1),
    }),

    accountMigration: z.toggleable({
        authMethod: z.enum(['selfAuth', 'apiKey', 'jwt']),
        validationEndpoint: z.url(),
        confirmationEndpoint: z.url(),
        createIdentityWithMigrationId: z.boolean(),
    }),
})

export type LocalAuthProvider = z.infer<typeof LocalAuthProviderSchema>
